import { createApp, nextTick } from 'vue';

import Api from '../api';

// Components
import Cart from './cart.vue';
import CartButton from './cart_button.vue';
import LineItemForm from './line_item_form.vue';
import LineItem from './line_item.vue';
import OrderForm from './order_form.vue';
import ProductGridFilter from './catalog/product_grid_filter.vue';

import ProductFilter from './catalog/product_filter.vue';
import ProductFilterButton from './catalog/product_filter_button.vue';

import normalizeParams from '../utilities/clean-object';

import SidemenuMenu from "./sidemenu/menu.vue";
import SidemenuButton from "./sidemenu/button.vue";

const shopAppInit = function() {

  const app = createApp({
    data: () => {
      return {
        cart: {
          lineItems: [],
          error: '',
          price: {
            totalPrice: 0,
            deliveryPrice: 0,
            discount: 0,
          }
        },
        filter: {
          loadedProducts: [],
          nextPage: null,
          params: {},
          currentPage: 1,
          totalItems: null,
        },
        loadingState: false,
        order: {
          errors: [],
          deliveryKind: 'courier',
          promocodeId: '',
          city: 'Москва'
        },
        priceLoadingState: false,
        promocodeApplied: false,
        searchVisible: false,
        sidemenuToggle: {
          'filter': false,
          'cart': false,
          'navigation': false,
        },
      };
    },

    created () {
      Api.getCart().then((r) => {
        this.cart.lineItems = r.data.lineItems;
        this.cart.price = r.data.price;
      });
    },

    mounted() {
      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('click', this.handleDocumentClick);

      nextTick(() => {
        const event = new Event('shopAppMounted');
        document.dispatchEvent(event);
      });
    },

    beforeUnmount() {
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('click', this.handleDocumentClick);
    },

    methods: {
      syncBodyScrollLock: function() {
        const menuOpen = Object.values(this.sidemenuToggle).some(Boolean);
        document.body.classList.toggle('scroll-y-disabled', menuOpen);
      },

      toggleSidemenu: function(id) {
        this.searchVisible = false;
        this.sidemenuToggle[id] = !this.sidemenuToggle[id];
        this.syncBodyScrollLock();
      },

      closeSidemenu: function(id) {
        this.sidemenuToggle[id] = false;
        this.syncBodyScrollLock();
      },

      closeAllSidemenu: function() {
        Object.keys(this.sidemenuToggle).forEach((menuId) => {
          this.sidemenuToggle[menuId] = false;
        });
        this.syncBodyScrollLock();
      },

      toggleSearch: function() {
        const nextState = !this.searchVisible;

        if (nextState) {
          this.closeAllSidemenu();
        }

        this.searchVisible = nextState;

        if (nextState) {
          nextTick(() => {
            document.querySelector('.uk-shell-search-input')?.focus();
          });
        }
      },

      closeSearch: function() {
        this.searchVisible = false;
      },

      handleKeydown: function(event) {
        if (event.key !== 'Escape') {
          return;
        }

        this.closeSearch();
        this.closeAllSidemenu();
      },

      handleDocumentClick: function(event) {
        const loadMoreLink = event.target.closest('.catalog-load-more__button[href]');

        if (!loadMoreLink) {
          return;
        }

        const productGrid = loadMoreLink.closest('.product-grid');

        if (!productGrid) {
          return;
        }

        event.preventDefault();
        this.appendServerRenderedProducts(loadMoreLink, productGrid);
      },

      appendServerRenderedProducts: async function(loadMoreLink, productGrid) {
        if (loadMoreLink.dataset.loading === 'true') {
          return;
        }

        const originalLabel = loadMoreLink.textContent;

        loadMoreLink.dataset.loading = 'true';
        loadMoreLink.setAttribute('aria-busy', 'true');
        loadMoreLink.textContent = 'Загружаем...';

        try {
          const response = await fetch(loadMoreLink.href, {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });

          if (!response.ok) {
            throw new Error(`Load more request failed with status ${response.status}`);
          }

          const html = await response.text();
          const parser = new DOMParser();
          const nextDocument = parser.parseFromString(html, 'text/html');
          const nextGrid = nextDocument.querySelector('.product-grid');
          const currentItems = productGrid.querySelector('.product-grid__items');
          const nextItems = nextGrid?.querySelector('.product-grid__items');

          if (!nextGrid || !currentItems || !nextItems) {
            throw new Error('Next product grid markup not found');
          }

          Array.from(nextItems.children).forEach((item) => {
            currentItems.appendChild(item.cloneNode(true));
          });

          const currentFooter = productGrid.querySelector('.catalog-load-more');
          const nextFooter = nextGrid.querySelector('.catalog-load-more');

          if (currentFooter && nextFooter) {
            currentFooter.replaceWith(nextFooter);
          }
        } catch (error) {
          window.location = loadMoreLink.href;
          return;
        }

        loadMoreLink.dataset.loading = 'false';
        loadMoreLink.removeAttribute('aria-busy');
        loadMoreLink.textContent = originalLabel;
      },

      applyFilters: function(params) {
        this.fetchFilteredProducts(params);
      },

      fetchFilteredProducts: function(params, { append = false } = {}) {
        this.loadingState = true;
        // Clean params, preserve sort state, then reflect the active filters in the URL.
        const normalizedParams = { ...params };
        normalizeParams(normalizedParams);

        const nextParams = {
          ...normalizedParams,
          ...this.currentSortParams(),
        };

        if (!append) {
          delete nextParams.page;
        }

        Api.applyFilters(nextParams).then((r) => {
          const items = r.data.items || [];
          const pagination = r.data.pagination || {};

          this.filter['loadedProducts'] = append ? this.filter.loadedProducts.concat(items) : items;
          this.filter['currentPage'] = pagination.current_page || 1;
          this.filter['nextPage'] = pagination.next_page;
          this.filter['params'] = { ...normalizedParams, ...this.currentSortParams() };
          this.filter['totalItems'] = pagination.total_count || 0;

          if (!append) {
            this.updateCatalogUrl(nextParams);
          }

          // hide preloaded grid
          Array.from(document.getElementsByClassName('replacable-grid')).forEach((e) => {
            e.style.display = "none";
          });


          if (this.sidemenuToggle['filter']) {
           this.toggleSidemenu('filter');
          }
          this.loadingState = false;
        }).catch((err) => {
            if (typeof(Sentry) != 'undefined') {
              Sentry.captureException(err);
            }
            this.loadingState = false;
        })
      },

      loadMoreFilteredProducts: function() {
        if (!this.filter.nextPage || this.loadingState) {
          return;
        }

        this.fetchFilteredProducts({
          ...this.filter.params,
          page: this.filter.nextPage,
        }, { append: true });
      },

      currentSortParams: function() {
        const currentParams = new URLSearchParams(window.location.search)
        const sortParams = {}

        if (currentParams.get('sort_field')) {
          sortParams.sort_field = currentParams.get('sort_field')
        }

        if (currentParams.get('sort_order')) {
          sortParams.sort_order = currentParams.get('sort_order')
        }

        return sortParams
      },

      updateCatalogUrl: function(params) {
        const searchParams = new URLSearchParams()

        Object.entries(params).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((item) => {
              searchParams.append(`${key}[]`, item)
            })
            return
          }

          if (value !== undefined && value !== null && value !== '') {
            searchParams.set(key, value)
          }
        })

        const nextSearch = searchParams.toString()
        const nextUrl = nextSearch.length > 0 ? `${window.location.pathname}?${nextSearch}` : window.location.pathname

        window.history.replaceState({}, '', nextUrl)
      },


      destroyLineItem: function(itemId) {
        this.loadingState = true;

        Api.removeLineItem(itemId)
          .then((r) => {
            const removeIndex = this.cart.lineItems.findIndex((item) => { return item.id == itemId; });
            this.cart.lineItems.splice(removeIndex, 1);

            this.cart.price = r.data;
            this.loadingState = false;
          }).catch((err) => {
            if (typeof(Sentry) != 'undefined') {
              Sentry.captureException(err);
            }
            this.loadingState = false;
          });
      },

      addToCart: function(params) {
        this.loadingState = true;

        Api.addToCart(params.productId, params.variantId).then((r) => {
          this.cart.lineItems = [r.data.lineItem, ...this.cart.lineItems];
          this.cart.price = r.data.price;

          this.loadingState = false;
          this.toggleSidemenu('cart');

          try {
            ym(36471355,'reachGoal','add-to-cart');
          } catch (err){
            if (typeof(Sentry) != 'undefined') {
              Sentry.captureException(err);
              Sentry.captureException(this.cart);
            }
          }
        }).catch((err) => {
          if (err.response.status == 422) {
            this.cart.error = err.response.data.error.message;
          } else {
            this.cart.error = 'Что-то пошло не так. Приносим извинения за доставленные неудобства!';
          }
          this.loadingState = false;
        });

      },

      getPrice: function() {
        this.priceLoadingState = true;
        Api.getPrice({
          'delivery_kind': this.order.deliveryKind,
          'promocode': this.order.promocode,
          'city': this.order.city
        }).then((r) => {
          this.cart.price = r.data;
          this.priceLoadingState = false;
          this.promocodeApplied = this.cart.price?.discount != 0;
        });
      },

      createOrder: function(orderParams) {
  if (this.loadingState) return;
  this.loadingState = true;

  Api.createOrder(orderParams)
    .then((r) => {
      this.loadingState = false;
      try {
        ym(36471355, 'reachGoal', 'order_complete');
      } catch (err) {
        if (typeof Sentry !== 'undefined') {
          Sentry.captureException(err);
        }
      }
      window.location.href = `/orders/${r.data.order.uuid}?success=true`;
      this.cart.lineItems = [];
    })
    .catch((err) => {
      let newErrors = [];

      if (err.response && err.response.status === 422) {
        // Handle validation errors
        const responseData = err.response.data;

        // Case 1: Errors are in `error.errors`
        if (responseData.error && Array.isArray(responseData.error.errors)) {
          newErrors = responseData.error.errors;
        }
        // Case 2: Errors are in `errors` (flat array)
        else if (Array.isArray(responseData.errors)) {
          newErrors = responseData.errors;
        }
        // Case 3: Errors are in `errors` (object with field-specific errors)
        else if (responseData.errors && typeof responseData.errors === 'object') {
          newErrors = Object.values(responseData.errors).flat();
        }
        // Case 4: Fallback for unexpected error format
        else {
          newErrors = ['Произошла ошибка при обработке вашего запроса.'];
        }
      } else {
        // Handle other errors (e.g., network errors, server errors)
        newErrors = [
          'При создании заказа что-то пошло не так.',
          'Пожалуйста, попробуйте еще раз или позвоните по любому номеру.',
        ];
      }

      this.loadingState = false;
      this.order.errors = newErrors;
    });
},
    }
  });

  app.component('Cart', Cart);
  app.component('CartButton', CartButton);
  app.component('LineItemForm', LineItemForm);
  app.component('LineItem', LineItem);
  app.component('OrderForm', OrderForm);
  app.component('ProductGridFilter', ProductGridFilter);
  app.component('ProductFilter', ProductFilter);
  app.component('ProductFilterButton', ProductFilterButton);
  app.component('SidemenuMenu', SidemenuMenu);
  app.component('SidemenuButton', SidemenuButton);

  app.config.compilerOptions.isCustomElement = (tag) => tag === 'oembed';

  return app.mount('#app');
};

export default shopAppInit;
